import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Box, Card, CardHeader, IconButton, Slider, Typography } from '@mui/material';
import { ZoomIn, ZoomOut, RestartAlt } from '@mui/icons-material';

export default function TempHappydemicsPdfImages() {
    const images = useMemo(() => {
        const mods = import.meta.glob('./Analysis Dashboard/*.{jpg,jpeg,png}', {
            eager: true,
            query: '?url',
            import: 'default',
        }) as Record<string, string>;

        const items = Object.entries(mods).map(([path, url]) => ({ path, url }));
        items.sort((a, b) => {
            const num = (s: string) => {
                const m = s.match(/(\d+)(?=\.\w+$)/);
                return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
            };
            return num(a.path) - num(b.path);
        });

        return items.map(i => i.url);
    }, []);

    const MIN = 0.25; // 25%
    const MAX = 1.0; // 100% cap
    const STEP = 0.05;

    const [scale, setScale] = useState(1); // 1 = 100% (fit width)
    const containerRef = useRef<HTMLDivElement | null>(null);

    const clamp = (v: number) => Math.min(MAX, Math.max(MIN, v));
    const setZoom = useCallback((next: number) => setScale(clamp(next)), []);
    const resetZoom = useCallback(() => setZoom(1), [setZoom]);

    const zoomWithAnchor = (delta: number) => {
        const el = containerRef.current;
        const prev = scale;
        const next = clamp(prev + delta);
        if (!el || next === prev) return;

        const { scrollLeft, scrollTop, clientWidth, clientHeight } = el;
        const cx = scrollLeft + clientWidth / 2;
        const cy = scrollTop + clientHeight / 2;
        const r = next / prev || 1;

        el.scrollTo({ left: cx * r - clientWidth / 2, top: cy * r - clientHeight / 2 });
        setScale(next);
    };

    return (
        <Card>
            <CardHeader
                title="Conversion Measurement"
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                        <IconButton
                            aria-label="Reset zoom"
                            onClick={resetZoom}
                            size="small"
                            title="Reset (100%)"
                        >
                            <RestartAlt fontSize="small" />
                        </IconButton>
                        <IconButton
                            aria-label="Zoom out"
                            onClick={() => zoomWithAnchor(-STEP)}
                            size="small"
                            title="Zoom out"
                            disabled={scale <= MIN}
                        >
                            <ZoomOut fontSize="small" />
                        </IconButton>
                        <IconButton
                            aria-label="Zoom in"
                            onClick={() => zoomWithAnchor(+STEP)}
                            size="small"
                            title="Zoom in"
                            disabled={scale >= MAX}
                        >
                            <ZoomIn fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: 56, textAlign: 'right' }}>
                            {Math.round(scale * 100)}%
                        </Typography>
                        <Box sx={{ width: 160, px: 1 }}>
                            <Slider
                                size="small"
                                value={scale}
                                min={MIN}
                                max={MAX}
                                step={STEP}
                                onChange={(_, v) => setZoom(v as number)}
                                aria-label="Zoom"
                            />
                        </Box>
                    </Box>
                }
            />

            <Box
                ref={containerRef}
                sx={{
                    height: '85vh',
                    overflow: 'auto',
                    bgcolor: t => t.palette.background.default,
                    display: 'flex',
                    justifyContent: 'center',
                }}
                onWheel={e => {
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        zoomWithAnchor(e.deltaY > 0 ? -STEP : +STEP);
                    }
                }}
            >
                <Box sx={{ width: `${scale * 100}%`, maxWidth: 'none' }}>
                    {images.map((src, idx) => (
                        <img
                            key={src}
                            src={src}
                            alt={`Page ${idx + 1}`}
                            loading={idx < 2 ? 'eager' : 'lazy'}
                            style={{
                                display: 'block',
                                width: '100%',
                                height: 'auto',
                                background: '#f3f3f3',
                            }}
                            onError={e => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </Card>
    );
}
